// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the browse panel's My data tab (docs/AYNI_DATA_INTAKE_PLAN.md,
 * phase 1: single files, private). For a plan with own data (Researcher,
 * Lab, or `xr-cap-own-data`):
 *
 * - **the list** of the account's and its lab's datasets (`GET
 *   /api/data/datasets`): title, state, size, whose, the quota, read-only
 *   after a lapse; Open, Details, the custody record, Delete (the owner,
 *   after typing the title);
 * - **Upload**: a file encrypted in this browser and sent in parts by a
 *   worker (`ownDataUploader.ts`), with progress, Cancel and Resume.
 *   Cancel (and "Cancel this upload" on the job page of an interrupted
 *   or not yet opened upload) asks the site to cancel the job, which
 *   destroys the key of what was sent and frees the slot at once
 *   (`POST /api/data/uploads/:job/cancel`, the uploader's own or any for
 *   the owner);
 * - **the job page**: where the job is (checking → inspecting → your
 *   choices → processing → review → published, or failed), the intake
 *   report, the choices form, and at review the error budget with the
 *   tolerance's pass or fail; Publish only when it passes.
 *
 * Phase 4 and 3 (myDataPanels.ts): **Shared with me** (for any signed-in
 * account, plan or not), **Share** per dataset (invitations, links, who
 * has access, the meeting it presents, open publishing when on), **Lab
 * members** (the owner sets roles), **Continuous sources**. Risky actions
 * ask for an emailed code first (myDataStepUp.ts, decision O4).
 *
 * Signed out, the tab is a locked button that opens the plans
 * (browseUI.ts); signed in without own data it shows what was shared with
 * the account and a locked upload; a class borrowing a teacher's tools
 * does not see it.
 *
 * **Everything shown here is text.** Variable names, units, file names,
 * titles, the processor's messages and other lab members' datasets come
 * from files and other people: the tab is built with DOM nodes and
 * `textContent` only, never HTML strings. Roles and plans are asked of the
 * site on every visit, never kept between page loads.
 */

import { plural, t, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { accountState, currentEntitlements } from '../services/account'
import { PALETTE_IDS } from '../services/colorScaleDisplay'
import { CANCELLABLE_JOB_STATES, REGRID_METHODS, type JobChoices, type JobState, type RegridMethod } from '../services/ownData'
import {
  JOB_STEPS,
  MOVING_STATES,
  cancelOwnUpload,
  custodyRecord,
  deleteOwnDataset,
  getJob,
  jobStepIndex,
  listOwnDatasets,
  publishJob,
  sharedWithMe,
  submitChoices,
  type DataFeatures,
  type DatasetList,
  type SharedDataset,
  type DatasetView,
  type IntakeReport,
  type JobView,
  type ReportVariable,
} from '../services/ownDataApi'
import { startOwnDataUpload, type UploadHandle } from '../services/ownDataUploader'
import type { UploadEvent, UploadProgress } from '../services/ownDataUpload'
import { ensureOwnDataset, forgetOwnDataset } from '../services/ownDataOpen'
import { setReviewedBudget } from '../services/privateData'
import { parseErrorBudget, regridName, toleranceVerdict } from '../services/errorBudget'
import { dataToolsLent } from '../services/planAccess'
import { budgetTable } from './accuracySection'
import { button, h } from './myDataDom'
import { mountMembersPanel, mountSharePanel, mountSourcesPanel, sharedSection, type PanelContext } from './myDataPanels'
import { guarded } from './myDataStepUp'

export interface MyDataCallbacks {
  /** Load an own dataset on the globe (its session is already open). */
  onOpenDataset: (id: string) => void
  onSignIn: (trigger: HTMLElement) => void
  onPlans: (trigger: HTMLElement) => void
}

/** The file kinds phase 1 reads (the plan's "File formats"). */
export const ACCEPTED_EXTENSIONS = ['.nc', '.nc4', '.netcdf', '.cdf', '.tif', '.tiff', '.zip', '.h5', '.hdf5', '.he5', '.hdf', '.grb', '.grib', '.grb2', '.grib2'] as const

/** Whether this browser's account may use own data now. */
export function ownDataAllowed(): boolean {
  return accountState().status === 'signedIn' && currentEntitlements().ownData
}

/**
 * Whether the tab is shown at all: only where own data is set up on this
 * site (`/api/account` says so), and never to a class borrowing a
 * teacher's tools.
 */
export function myDataTabVisible(): boolean {
  return accountState().ownData && !dataToolsLent()
}

/** Whether a file name has one of the extensions phase 1 reads. */
export function acceptedFileName(name: string): boolean {
  const lower = name.toLowerCase()
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

/** Bytes for people: "1.2 GB", "640 MB" (decimal, as quotas count them). */
export function formatBytes(bytes: number): string {
  const units: Array<[number, MessageKey]> = [[1e12, 'myData.bytes.tb'], [1e9, 'myData.bytes.gb'], [1e6, 'myData.bytes.mb'], [1e3, 'myData.bytes.kb']]
  for (const [size, key] of units) {
    if (bytes >= size) return t(key, { n: formatNumber(bytes / size, { maximumSignificantDigits: 3 }) })
  }
  return t('myData.bytes.b', { n: formatNumber(bytes) })
}

/** A grid's spacing in degrees from its count and extent, or null. */
export function gridSpacing(axis: { n?: number; min?: number; max?: number } | undefined): number | null {
  if (!axis || typeof axis.n !== 'number' || axis.n < 2 || typeof axis.min !== 'number' || typeof axis.max !== 'number') return null
  const d = Math.abs(axis.max - axis.min) / (axis.n - 1)
  return d > 0 && Number.isFinite(d) ? d : null
}

/** The globe's finest grid: 4096 columns round the Earth (the plan's regrid target), never finer than the source. */
export function targetSpacing(source: number | null): number {
  const finest = 360 / 4096
  return source === null ? finest : Math.max(source, finest)
}

/**
 * A grid's longitudes as west and east for the region fields: a grid on
 * 0…360 that starts at 0 covers the whole circle, so it starts as the
 * whole globe (−180…180); one wholly east of 180 is moved to −180…0; any
 * other is kept (the processor takes an east edge up to 360).
 */
export function lonExtent(axis: { min?: number; max?: number } | undefined): [number, number] {
  if (!axis || typeof axis.min !== 'number' || typeof axis.max !== 'number') return [-180, 180]
  if (axis.max > 180 && axis.min <= 0) return [-180, 180]
  if (axis.min >= 180) return [axis.min - 360, axis.max - 360]
  return [axis.min, axis.max]
}

/** A report's instant for people, in UTC ("6 Oct 2026, 00:00 UTC"); as given when it is not a date. */
function instant(iso: string): string {
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? `${formatDate(d, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' })} UTC` : iso
}

/** "0.25°" */
function degrees(d: number): string {
  return t('myData.degrees', { n: formatNumber(d, { maximumSignificantDigits: 3 }) })
}

/**
 * The choices to send from what the form holds: the time span, region and
 * range only when they differ from the file's whole extent and the
 * default percentiles, so "unchanged" means "the default" to the processor.
 */
export function choicesFromForm(form: {
  variable: string
  palette: string
  regrid: RegridMethod
  time: { start: string; end: string } | null
  defaultTime: { start: string; end: string } | null
  region: [number, number, number, number] | null
  range: { min: number; max: number } | null
  defaultRange: { min: number; max: number } | null
  tolerance: { quantile: number; abs: number } | null
}): JobChoices {
  const out: JobChoices = { variable: form.variable, palette: form.palette, regrid: form.regrid }
  if (form.time && (!form.defaultTime || form.time.start !== form.defaultTime.start || form.time.end !== form.defaultTime.end)) {
    out.time = { start: new Date(form.time.start).toISOString(), end: new Date(form.time.end).toISOString() }
  }
  if (form.region) out.region = form.region
  if (form.range && (!form.defaultRange || form.range.min !== form.defaultRange.min || form.range.max !== form.defaultRange.max)) out.range = form.range
  if (form.tolerance) out.tolerance = form.tolerance
  return out
}

// ------------------------------------------------------------------ DOM

const STATE_LABEL: Record<JobState, MessageKey> = {
  uploading: 'myData.state.uploading',
  uploaded: 'myData.state.uploaded',
  checking: 'myData.state.checking',
  inspecting: 'myData.state.inspecting',
  awaiting_choices: 'myData.state.awaitingChoices',
  processing: 'myData.state.processing',
  review: 'myData.state.review',
  published: 'myData.state.published',
  failed: 'myData.state.failed',
  expired: 'myData.state.expired',
  cancelled: 'myData.state.cancelled',
}

const STEP_LABEL: Record<(typeof JOB_STEPS)[number], MessageKey> = {
  checking: 'myData.step.checking',
  inspecting: 'myData.step.inspecting',
  awaiting_choices: 'myData.step.choices',
  processing: 'myData.step.processing',
  review: 'myData.step.review',
  published: 'myData.step.published',
}

const ERROR_TEXT: Readonly<Record<string, MessageKey>> = {
  unreachable: 'myData.error.unreachable',
  signed_out: 'myData.error.signedOut',
  unauthorized: 'myData.error.signedOut',
  plan_required: 'myData.error.planRequired',
  role: 'myData.error.role',
  forbidden_origin: 'myData.error.generic',
  rate_limited: 'myData.error.rateLimited',
  not_configured: 'myData.error.notConfigured',
  not_found: 'myData.error.notFound',
  job_too_large: 'myData.error.tooLarge',
  quota_exceeded: 'myData.error.quota',
  too_many_jobs: 'myData.error.tooManyJobs',
  size_mismatch: 'myData.error.sizeMismatch',
  parts_incomplete: 'myData.error.partsIncomplete',
  bad_choices: 'myData.error.badChoices',
  wrong_state: 'myData.error.wrongState',
  intake_unreachable: 'myData.error.intakeUnreachable',
  tolerance_not_met: 'myData.error.toleranceNotMet',
  tolerance_not_the_customers: 'myData.error.toleranceNotYours',
  budget_changed: 'myData.error.budgetChanged',
  wrong_size: 'myData.error.sizeMismatch',
  release_not_publishable: 'myData.error.notPublishable',
  release_missing: 'myData.error.releaseChanged',
  release_changed: 'myData.error.releaseChanged',
  not_ready: 'myData.open.notReady',
  worker: 'myData.error.generic',
  network: 'myData.error.unreachable',
  step_up_required: 'myData.stepUp.needed',
  bad_email: 'myData.share.badEmail',
  self_share: 'myData.share.self',
  already_shared: 'myData.share.already',
  too_many_shares: 'myData.share.tooMany',
  bad_share: 'myData.error.generic',
  not_enabled: 'myData.error.notEnabled',
  not_presenter: 'myData.meeting.notPresenter',
  meeting_not_live: 'myData.meeting.notLive',
  rooms_unconfigured: 'myData.error.notConfigured',
  bad_confirmation: 'myData.openPub.badConfirm',
  own_role: 'myData.members.ownRole',
  not_holder: 'myData.members.notHolder',
  bad_role: 'myData.error.generic',
  source_quota: 'myData.source.full',
  bad_source: 'myData.source.bad.endpoint',
  link_ended: 'myData.shared.linkEnded',
  own_lab: 'myData.shared.ownLab',
  source_fed: 'myData.openPub.sourceFed',
  step_up_locked: 'myData.stepUp.locked',
  exact_not_allowed: 'myData.error.generic',
}

/** A route's error code in plain words. */
export function errorText(code: string): string {
  if (code.startsWith('custody')) return t('myData.error.custody')
  return t(ERROR_TEXT[code] ?? 'myData.error.generic')
}

// The processor's own failure codes, in plain words; its message follows.
const FAILURE_TEXT: Readonly<Record<string, MessageKey>> = {
  hash_mismatch: 'myData.failure.hashMismatch',
  unreadable: 'myData.failure.unreadable',
  unsupported_grid: 'myData.failure.unsupportedGrid',
  tolerance_unreachable: 'myData.failure.toleranceUnreachable',
  too_large: 'myData.failure.tooLarge',
  size_mismatch: 'myData.error.sizeMismatch',
}

// ---------------------------------------------------------------- state

type View =
  | { kind: 'locked'; why: 'signed_out' | 'plan_required' }
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'list' }
  | { kind: 'upload' }
  | { kind: 'job'; id: string }
  | { kind: 'delete'; dataset: DatasetView }
  | { kind: 'share'; dataset: DatasetView }
  | { kind: 'members' }
  | { kind: 'sources' }

interface UploadState {
  handle: UploadHandle
  fileName: string
  bytes: number
  status: 'running' | 'paused' | 'done' | 'failed' | 'cancelled'
  progress: UploadProgress | null
  job: string | null
  dataset: string | null
  code: string | null
}

/** One upload at a time, kept while the tab is redrawn or hidden (not over a reload: the key is in memory only). */
let upload: UploadState | null = null

function uploadBusy(): boolean {
  return upload?.status === 'running' || upload?.status === 'paused'
}

if (typeof window !== 'undefined') {
  // A reload cannot resume (the key is gone with the page): ask first.
  window.addEventListener('beforeunload', (e) => {
    if (uploadBusy()) e.preventDefault()
  })
}

const POLL_JOB_MS = 5000
const POLL_LIST_MS = 15000

export function mountMyDataTab(pane: HTMLElement, callbacks: MyDataCallbacks): { refresh: () => void } {
  let view: View = { kind: 'loading' }
  let list: DatasetList | null = null
  let job: JobView | null = null
  let notice: { text: string; tone: 'info' | 'error' } | null = null
  let shown = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let busy = false
  /** Phase 4: what was shared with this account, and which switchable features the site has on. */
  let shared: SharedDataset[] = []
  let features: DataFeatures = { stepUp: true, meetingShares: false, openPublishing: false }
  /** Where the step-up box is drawn; one element kept across redraws, so a box being answered survives them. */
  const stepSlot = h('div', { class: 'mydata-stepup-slot' })
  const panelBox = h('div', { class: 'mydata-panel' })
  let panel: { kind: 'share' | 'members' | 'sources'; id: string | null; ctl: { refresh: () => void } } | null = null
  const panelContext = (): PanelContext => ({ stepSlot, errorText, features, role: list?.tenant?.role ?? null, readOnly: list?.tenant?.readOnly === true })
  /** The job page asks "Cancel this upload?" before it does. */
  let confirmCancel = false

  const visible = () => shown && pane.isConnected && !pane.classList.contains('hidden')

  function stopPolling(): void {
    if (timer) clearTimeout(timer)
    timer = null
  }

  function poll(ms: number, fn: () => Promise<void>): void {
    stopPolling()
    timer = setTimeout(() => {
      timer = null
      if (!visible()) return
      void fn()
    }, ms)
  }

  async function loadShared(): Promise<void> {
    const reply = await sharedWithMe()
    if (reply.ok) {
      shared = reply.value.datasets
      features = reply.value.features
    }
  }

  async function loadList(quiet = false): Promise<void> {
    if (!ownDataAllowed()) {
      const signedIn = accountState().status === 'signedIn'
      view = { kind: 'locked', why: signedIn ? 'plan_required' : 'signed_out' }
      render()
      if (signedIn) {
        await loadShared()
        if (view.kind === 'locked') render()
      }
      return
    }
    void loadShared().then(() => {
      if (view.kind === 'list') render()
    })
    if (!quiet && !list) {
      view = { kind: 'loading' }
      render()
    }
    const reply = await listOwnDatasets()
    if (!reply.ok) {
      if (reply.error === 'plan_required' || reply.error === 'signed_out' || reply.error === 'unauthorized') {
        view = { kind: 'locked', why: reply.error === 'plan_required' ? 'plan_required' : 'signed_out' }
      } else if (view.kind === 'list' || view.kind === 'loading') {
        view = { kind: 'error', code: reply.error }
      }
      render()
      return
    }
    list = reply.value
    for (const d of list.datasets) if (d.job?.budget) setReviewedBudget(d.id, d.job.budget)
    if (view.kind === 'loading' || view.kind === 'error' || view.kind === 'locked') view = { kind: 'list' }
    if (view.kind === 'list') render()
    if (view.kind === 'list' && list.datasets.some((d) => d.job && MOVING_STATES.includes(d.job.state))) poll(POLL_LIST_MS, () => loadList(true))
  }

  async function loadJob(id: string): Promise<void> {
    const reply = await getJob(id)
    if (view.kind !== 'job' || view.id !== id) return
    if (!reply.ok) {
      notice = { text: errorText(reply.error), tone: 'error' }
      render()
      if (reply.status === 0 || reply.status >= 500 || reply.status === 429) poll((reply.retryAfterS ?? 15) * 1000, () => loadJob(id))
      return
    }
    const changed = !job || job.id !== id || job.state !== reply.value.job.state || job.updatedAt !== reply.value.job.updatedAt
    // A note about the last step ("uploaded", "choices sent") is stale once the job moves on.
    if (job && job.id === id && job.state !== reply.value.job.state && notice?.tone === 'info') notice = null
    job = reply.value.job
    if (job.budget) setReviewedBudget(job.dataset, job.budget)
    if (changed) render()
    if (MOVING_STATES.includes(job.state)) poll(POLL_JOB_MS, () => loadJob(id))
  }

  function go(next: View): void {
    stopPolling()
    if (next.kind !== 'share' && next.kind !== 'members' && next.kind !== 'sources') panel = null
    view = next
    notice = null
    if (next.kind !== 'job') job = null
    confirmCancel = false
    render()
    if (next.kind === 'list') void loadList(true)
    if (next.kind === 'job') void loadJob(next.id)
  }

  // ---------------------------------------------------------- pieces

  function noticeLine(): HTMLElement | null {
    if (!notice) return null
    return h('p', { class: `mydata-notice${notice.tone === 'error' ? ' is-error' : ''}`, role: notice.tone === 'error' ? 'alert' : 'status' }, notice.text)
  }

  function head(title: string, back: boolean): HTMLElement {
    return h('div', { class: 'pl-tab-detail-head mydata-head' },
      back ? h('button', { type: 'button', class: 'pl-tab-icon pl-tab-back mydata-back', 'aria-label': t('myData.back.aria') }, '‹') : null,
      h('h3', { class: 'pl-tab-detail-name mydata-title' }, title))
  }

  function quotaBlock(l: DatasetList): HTMLElement | null {
    if (!l.quota) return null
    const used = l.quota.usedBytes
    const cap = l.quota.storedBytes
    const share = cap > 0 ? Math.min(1, used / cap) : 0
    const bar = h('div', { class: 'mydata-quota-bar', role: 'meter', 'aria-valuemin': 0, 'aria-valuemax': cap, 'aria-valuenow': used, 'aria-label': t('myData.quota.aria') },
      h('span', { class: 'mydata-quota-fill', style: `inline-size: ${(share * 100).toFixed(1)}%` }))
    return h('div', { class: 'mydata-quota' },
      h('p', { class: 'mydata-quota-text' }, t('myData.quota.used', { used: formatBytes(used), total: formatBytes(cap) })),
      bar,
      h('p', { class: 'mydata-quota-note' }, t('myData.quota.largest', { size: formatBytes(l.quota.jobBytes) })))
  }

  function progressBlock(u: UploadState): HTMLElement {
    const p = u.progress
    const total = p?.totalBytes ?? u.bytes
    const sent = p?.sentBytes ?? 0
    const pct = total > 0 ? Math.round((sent / total) * 100) : 0
    let text: string
    if (u.status === 'paused') text = t('myData.upload.paused')
    else if (!p || p.phase === 'starting') text = t('myData.upload.starting')
    else if (p.phase === 'completing') text = t('myData.upload.completing')
    else text = t('myData.upload.sending', { percent: pct, part: Math.min(p.partCount, p.partsStored + 1), parts: p.partCount })
    return h('div', { class: 'mydata-upload-progress', 'data-status': u.status },
      h('p', { class: 'mydata-upload-file' }, t('myData.upload.file', { name: u.fileName, size: formatBytes(u.bytes) })),
      h('div', { class: 'mydata-progress-bar', role: 'progressbar', 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': pct, 'aria-label': t('myData.upload.progressAria') },
        h('span', { class: 'mydata-progress-fill', style: `inline-size: ${pct}%` })),
      h('p', { class: 'mydata-upload-status', role: 'status' }, text),
      h('div', { class: 'mydata-actions' },
        u.status === 'paused' ? button('pl-tab-btn mydata-resume', t('myData.upload.resume')) : null,
        button('pl-tab-btn pl-tab-btn-quiet mydata-cancel', t('myData.upload.cancel'))))
  }

  function updateProgressInPlace(): void {
    const old = pane.querySelector('.mydata-upload-progress')
    if (old && upload && (upload.status === 'running' || upload.status === 'paused')) old.replaceWith(progressBlock(upload))
    else render()
  }

  function rowState(d: DatasetView): { text: string; state: string } {
    if (d.state === 'ready') return { text: t('myData.state.published'), state: 'published' }
    const s = d.job?.state
    if (!s) return { text: t('myData.state.pending'), state: 'pending' }
    if (s === 'uploading' && upload?.dataset !== d.id) return { text: t('myData.state.interrupted'), state: 'interrupted' }
    return { text: t(STATE_LABEL[s]), state: s }
  }

  function datasetRow(d: DatasetView, role: string | null): HTMLElement {
    const st = rowState(d)
    const owner = role === 'owner'
    const meta = [
      formatBytes(d.bytes),
      d.mine ? t('myData.row.mine') : t('myData.row.lab'),
      formatDate(new Date(d.createdAt), { dateStyle: 'medium' }),
      d.shares ? plural(d.shares, { one: 'myData.row.shares.one', other: 'myData.row.shares.other' }) : '',
      d.open ? t('myData.row.open') : '',
    ].filter(Boolean).join(' · ')
    const mayShare = d.state === 'ready' && list?.tenant?.readOnly !== true && (owner || (role === 'member' && d.mine))
    return h('li', { class: 'mydata-row', 'data-id': d.id },
      h('div', { class: 'mydata-row-main' },
        h('span', { class: 'mydata-row-title' }, d.title),
        h('span', { class: 'mydata-chip', 'data-state': st.state }, st.text)),
      h('span', { class: 'mydata-row-meta' }, meta),
      h('div', { class: 'mydata-actions' },
        d.state === 'ready' ? button('pl-tab-btn mydata-open', t('myData.open'), { 'data-id': d.id, 'aria-label': t('myData.open.aria', { title: d.title }) }) : null,
        d.job ? button('pl-tab-btn pl-tab-btn-quiet mydata-details', t('myData.details'), { 'data-job': d.job.id, 'aria-label': t('myData.details.aria', { title: d.title }) }) : null,
        mayShare ? button('pl-tab-btn pl-tab-btn-quiet mydata-share', t('myData.share'), { 'data-id': d.id, 'aria-label': t('myData.share.aria', { title: d.title }) }) : null,
        owner || d.mine ? button('pl-tab-btn pl-tab-btn-quiet mydata-custody', t('myData.custody'), { 'data-id': d.id, 'aria-label': t('myData.custody.aria', { title: d.title }) }) : null,
        owner ? button('pl-tab-btn pl-tab-btn-quiet mydata-delete', t('myData.delete'), { 'data-id': d.id, 'aria-label': t('myData.delete.aria', { title: d.title }) }) : null))
  }

  // ----------------------------------------------------------- views

  function renderLocked(why: 'signed_out' | 'plan_required'): HTMLElement {
    return h('div', { class: 'mydata-locked' },
      // Signed in without own data: what others shared with this account comes first.
      why === 'plan_required' ? sharedSection(shared) : null,
      noticeLine(),
      h('p', { class: 'mydata-locked-text' }, t(why === 'signed_out' ? 'myData.locked.signedOut' : 'myData.locked.plan')),
      h('div', { class: 'mydata-actions' },
        why === 'signed_out' ? button('pl-tab-btn mydata-signin', t('myData.signIn')) : null,
        button('pl-tab-btn pl-tab-btn-quiet mydata-plans', t('myData.plans'))))
  }

  function renderList(): HTMLElement {
    const l = list!
    const role = l.tenant?.role ?? null
    const readOnly = l.tenant?.readOnly === true
    const canUpload = !readOnly && (role === null || role === 'owner' || role === 'member')
    const wrap = h('div', { class: 'mydata-list-view' },
      h('p', { class: 'mydata-intro' }, t('myData.intro')),
      readOnly ? h('p', { class: 'mydata-notice is-error', role: 'status' }, t('myData.readOnly')) : null,
      quotaBlock(l),
      noticeLine(),
      upload && (upload.status === 'running' || upload.status === 'paused') ? progressBlock(upload) : null,
      h('div', { class: 'mydata-actions' },
        button('pl-tab-btn mydata-upload-open', t('myData.upload.open'), { disabled: !canUpload || uploadBusy() }),
        h('span', { class: 'mydata-formats' }, t('myData.formats'))),
      h('div', { class: 'mydata-actions mydata-lab-actions' },
        // A viewer does not see sources (review I-6).
        role === 'owner' || role === 'member' ? button('pl-tab-btn pl-tab-btn-quiet mydata-sources-open', t('myData.source.title')) : null,
        role === 'owner' && l.tenant?.kind === 'lab' ? button('pl-tab-btn pl-tab-btn-quiet mydata-members-open', t('myData.members.title')) : null))
    if (shared.length) wrap.append(sharedSection(shared))
    if (!l.datasets.length) {
      wrap.append(h('div', { class: 'pl-tab-empty' }, t('myData.empty')))
      return wrap
    }
    wrap.append(h('p', { class: 'mydata-count', role: 'status' }, plural(l.datasets.length, { one: 'myData.count.one', other: 'myData.count.other' })))
    const ul = h('ul', { class: 'mydata-list', role: 'list' })
    for (const d of l.datasets) ul.append(datasetRow(d, role))
    wrap.append(ul)
    return wrap
  }

  function renderUpload(): HTMLElement {
    const max = list?.quota?.jobBytes ?? null
    const wrap = h('div', { class: 'mydata-upload' }, head(t('myData.upload.title'), true))
    if (upload && upload.status !== 'cancelled' && upload.status !== 'failed' && upload.status !== 'done') {
      wrap.append(progressBlock(upload), noticeLine() ?? '')
      return wrap
    }
    const file = h('input', { type: 'file', id: 'mydata-file', class: 'mydata-file', accept: ACCEPTED_EXTENSIONS.join(','), required: true })
    const title = h('input', { type: 'text', id: 'mydata-name', class: 'mydata-input mydata-name', maxlength: 200, autocomplete: 'off' })
    wrap.append(
      h('p', { class: 'mydata-intro' }, t('myData.upload.intro')),
      h('label', { class: 'mydata-label', for: 'mydata-file' }, t('myData.upload.fileLabel')),
      file,
      h('p', { class: 'mydata-hint' }, max !== null ? t('myData.upload.limit', { size: formatBytes(max) }) : t('myData.formats')),
      h('label', { class: 'mydata-label', for: 'mydata-name' }, t('myData.upload.nameLabel')),
      title,
      h('p', { class: 'mydata-hint' }, t('myData.upload.privacy')),
      noticeLine() ?? '',
      h('div', { class: 'mydata-actions' }, button('pl-tab-btn mydata-upload-start', t('myData.upload.start'))))
    return wrap
  }

  function stepper(state: JobState): HTMLElement {
    const at = jobStepIndex(state)
    const failed = state === 'failed' || state === 'expired' || state === 'cancelled'
    const ol = h('ol', { class: 'mydata-steps', 'aria-label': t('myData.steps.aria') })
    JOB_STEPS.forEach((step, i) => {
      const status = failed ? (i < Math.max(0, at) ? 'done' : 'todo') : i < at ? 'done' : i === at ? 'current' : 'todo'
      ol.append(h('li', { class: 'mydata-step', 'data-status': status, 'aria-current': status === 'current' ? 'step' : undefined }, t(STEP_LABEL[step])))
    })
    return ol
  }

  function reportBlock(report: IntakeReport): HTMLElement {
    const box = h('section', { class: 'mydata-report', 'aria-label': t('myData.report.title') }, h('h4', null, t('myData.report.title')))
    const facts: Array<[string, string]> = []
    if (report.format) facts.push([t('myData.report.format'), report.format + (report.conventions ? ` · ${report.conventions}` : '')])
    const g = report.grid
    if (g) {
      const lat = g.lat
      const lon = g.lon
      const parts = [g.type ?? '', lat ? t('myData.report.axis', { name: t('myData.report.lat'), n: formatNumber(lat.n ?? 0), min: formatNumber(lat.min ?? 0), max: formatNumber(lat.max ?? 0) }) : '', lon ? t('myData.report.axis', { name: t('myData.report.lon'), n: formatNumber(lon.n ?? 0), min: formatNumber(lon.min ?? 0), max: formatNumber(lon.max ?? 0) }) : '', g.crs ?? '']
      facts.push([t('myData.report.grid'), parts.filter(Boolean).join(' · ')])
    }
    const tm = report.time
    if (tm) {
      const span = tm.start && tm.end ? `${instant(tm.start)} – ${instant(tm.end)}` : ''
      const cadence = tm.cadenceSeconds ? t('myData.report.cadence', { hours: formatNumber(tm.cadenceSeconds / 3600, { maximumSignificantDigits: 3 }) }) : ''
      facts.push([t('myData.report.time'), [tm.steps !== undefined ? plural(tm.steps, { one: 'myData.report.steps.one', other: 'myData.report.steps.other' }) : '', span, cadence, tm.calendar ?? ''].filter(Boolean).join(' · ')])
    }
    if (report.estimate) {
      const e = report.estimate
      facts.push([t('myData.report.estimate'), [e.outputBytes ? formatBytes(e.outputBytes) : '', e.minutes ? t('myData.report.minutes', { n: formatNumber(e.minutes) }) : ''].filter(Boolean).join(' · ')])
    }
    if (facts.length) {
      const dl = h('dl', { class: 'mydata-facts' })
      for (const [k, v] of facts) dl.append(h('dt', null, k), h('dd', null, v))
      box.append(dl)
    }
    const table = h('table', { class: 'mydata-vars' },
      h('caption', { class: 'sr-only' }, t('myData.report.variables')),
      h('thead', null, h('tr', null,
        h('th', { scope: 'col' }, t('myData.report.col.name')),
        h('th', { scope: 'col' }, t('myData.report.col.units')),
        h('th', { scope: 'col' }, t('myData.report.col.shape')))))
    const body = h('tbody')
    for (const v of report.variables ?? []) {
      const dims = v.dims?.length ? v.dims.map((d, i) => (v.shape?.[i] !== undefined ? `${d} ${formatNumber(v.shape[i])}` : d)).join(' × ') : ''
      body.append(h('tr', null,
        h('td', null, h('span', { class: 'mydata-var-name' }, String(v.name)), v.longName ? h('span', { class: 'mydata-var-long' }, String(v.longName)) : null),
        h('td', null, v.units ? String(v.units) : '–'),
        h('td', null, [dims, v.dtype ?? ''].filter(Boolean).join(' · '))))
    }
    table.append(body)
    box.append(table)
    if (report.warnings?.length) {
      box.append(h('h4', null, t('myData.report.warnings')))
      const ul = h('ul', { class: 'mydata-warnings' })
      for (const w of report.warnings) ul.append(h('li', null, String(w)))
      box.append(ul)
    }
    return box
  }

  function variableUnits(report: IntakeReport, name: string): string | null {
    return report.variables.find((v) => v.name === name)?.units ?? null
  }

  function defaultRange(v: ReportVariable | undefined): { min: number; max: number } | null {
    const lo = v?.percentiles?.p0_1
    const hi = v?.percentiles?.p99_9
    return typeof lo === 'number' && typeof hi === 'number' && lo < hi ? { min: lo, max: hi } : null
  }

  /** "2020-01-01T00:00" for a datetime-local input, in UTC. */
  function localInput(iso: string | undefined): string {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 16) : ''
  }

  function choicesForm(j: JobView, report: IntakeReport): HTMLElement {
    const vars = report.variables ?? []
    const first = vars[0]
    const src = gridSpacing(report.grid?.lon) ?? gridSpacing(report.grid?.lat)
    const target = targetSpacing(src)
    const range = defaultRange(first)
    const form = h('form', { class: 'mydata-choices', novalidate: true, 'aria-label': t('myData.choices.title') }, h('h4', null, t('myData.choices.title')))
    const field = (label: string, id: string, control: HTMLElement, hint?: string) =>
      h('div', { class: 'mydata-field' }, h('label', { class: 'mydata-label', for: id }, label), control, hint ? h('p', { class: 'mydata-hint' }, hint) : null)

    const variable = h('select', { id: 'mydata-variable', class: 'mydata-input', name: 'variable' })
    for (const v of vars) variable.append(h('option', { value: String(v.name) }, [String(v.name), v.longName ? String(v.longName) : '', v.units ? `(${String(v.units)})` : ''].filter(Boolean).join(' · ')))
    form.append(field(t('myData.choices.variable'), 'mydata-variable', variable))

    const tStart = h('input', { type: 'datetime-local', id: 'mydata-time-start', class: 'mydata-input', name: 'timeStart', value: localInput(report.time?.start) })
    const tEnd = h('input', { type: 'datetime-local', id: 'mydata-time-end', class: 'mydata-input', name: 'timeEnd', value: localInput(report.time?.end), 'aria-label': t('myData.choices.timeEnd') })
    if (report.time?.start) {
      form.append(h('div', { class: 'mydata-field' },
        h('label', { class: 'mydata-label', for: 'mydata-time-start' }, t('myData.choices.time')),
        h('div', { class: 'mydata-pair' }, tStart, h('span', { 'aria-hidden': 'true' }, '–'), tEnd),
        h('p', { class: 'mydata-hint' }, t('myData.choices.timeHint'))))
    }

    const regionOn = h('input', { type: 'checkbox', id: 'mydata-region-on', name: 'regionOn' })
    const lat = report.grid?.lat
    const lon = report.grid?.lon
    const num = (id: string, label: string, value: number | undefined) =>
      h('input', { type: 'number', id, class: 'mydata-input mydata-num', step: 'any', 'aria-label': label, value: value !== undefined ? String(value) : '', disabled: true })
    const [w0, e0] = lonExtent(lon)
    const west = num('mydata-west', t('myData.choices.west'), w0)
    const south = num('mydata-south', t('myData.choices.south'), lat?.min ?? -90)
    const east = num('mydata-east', t('myData.choices.east'), e0)
    const north = num('mydata-north', t('myData.choices.north'), lat?.max ?? 90)
    form.append(h('div', { class: 'mydata-field' },
      h('label', { class: 'mydata-check', for: 'mydata-region-on' }, regionOn, t('myData.choices.region')),
      h('div', { class: 'mydata-region' }, west, south, east, north),
      h('p', { class: 'mydata-hint' }, t('myData.choices.regionHint'))))
    regionOn.addEventListener('change', () => {
      for (const i of [west, south, east, north]) i.disabled = !regionOn.checked
    })

    const palette = h('select', { id: 'mydata-palette', class: 'mydata-input', name: 'palette' })
    for (const id of PALETTE_IDS) if (id !== 'source') palette.append(h('option', { value: id, selected: id === 'viridis' }, t(`colorbar.palette.${id}` as MessageKey)))
    form.append(field(t('myData.choices.palette'), 'mydata-palette', palette))

    const rMin = h('input', { type: 'number', id: 'mydata-min', class: 'mydata-input mydata-num', step: 'any', value: range ? String(range.min) : '', 'aria-label': t('myData.choices.min') })
    const rMax = h('input', { type: 'number', id: 'mydata-max', class: 'mydata-input mydata-num', step: 'any', value: range ? String(range.max) : '', 'aria-label': t('myData.choices.max') })
    const unitsOut = h('span', { class: 'mydata-units' }, first?.units ? String(first.units) : '')
    form.append(h('div', { class: 'mydata-field' },
      h('label', { class: 'mydata-label', for: 'mydata-min' }, t('myData.choices.range')),
      h('div', { class: 'mydata-pair' }, rMin, h('span', { 'aria-hidden': 'true' }, '–'), rMax, unitsOut),
      h('p', { class: 'mydata-hint' }, t('myData.choices.rangeHint'))))

    const regrid = h('fieldset', { class: 'mydata-field mydata-regrid' }, h('legend', { class: 'mydata-label' }, t('myData.choices.regrid')))
    for (const m of REGRID_METHODS) {
      const id = `mydata-regrid-${m}`
      regrid.append(h('label', { class: 'mydata-check', for: id }, h('input', { type: 'radio', name: 'regrid', id, value: m, checked: m === 'bilinear' }), regridName(m)))
    }
    regrid.append(h('p', { class: 'mydata-hint' }, src !== null
      ? t('myData.choices.regridHint', { from: degrees(src), to: degrees(target) })
      : t('myData.choices.regridHintUnknown', { to: degrees(target) })))
    form.append(regrid)

    const tolOn = h('input', { type: 'checkbox', id: 'mydata-tol-on', name: 'tolOn' })
    const quantile = h('select', { id: 'mydata-tol-q', class: 'mydata-input', disabled: true, 'aria-label': t('myData.choices.toleranceShare') })
    for (const q of [0.9, 0.95, 0.99, 0.999]) quantile.append(h('option', { value: String(q), selected: q === 0.99 }, formatNumber(q, { style: 'percent', maximumSignificantDigits: 4 })))
    const abs = h('input', { type: 'number', id: 'mydata-tol-abs', class: 'mydata-input mydata-num', step: 'any', min: '0', disabled: true, 'aria-label': t('myData.choices.toleranceAbs') })
    const tolUnits = h('span', { class: 'mydata-units' }, first?.units ? String(first.units) : '')
    form.append(h('div', { class: 'mydata-field' },
      h('label', { class: 'mydata-check', for: 'mydata-tol-on' }, tolOn, t('myData.choices.tolerance')),
      h('div', { class: 'mydata-pair' }, quantile, h('span', null, t('myData.choices.toleranceWithin')), abs, tolUnits),
      h('p', { class: 'mydata-hint' }, t('myData.choices.toleranceHint'))))
    tolOn.addEventListener('change', () => {
      quantile.disabled = !tolOn.checked
      abs.disabled = !tolOn.checked
      if (tolOn.checked) abs.focus()
    })

    variable.addEventListener('change', () => {
      const v = vars.find((x) => x.name === variable.value)
      const r = defaultRange(v)
      rMin.value = r ? String(r.min) : ''
      rMax.value = r ? String(r.max) : ''
      const u = v?.units ? String(v.units) : ''
      unitsOut.textContent = u
      tolUnits.textContent = u
    })

    const error = h('p', { class: 'mydata-notice is-error', role: 'alert', hidden: true })
    const submit = h('button', { type: 'submit', class: 'pl-tab-btn mydata-process' }, t('myData.choices.submit'))
    form.append(error, h('div', { class: 'mydata-actions' }, submit))

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      if (busy) return
      const fail = (key: MessageKey) => {
        error.textContent = t(key)
        error.hidden = false
      }
      error.hidden = true
      const v = vars.find((x) => x.name === variable.value)
      const minV = rMin.value.trim() === '' ? null : Number(rMin.value)
      const maxV = rMax.value.trim() === '' ? null : Number(rMax.value)
      let rangeChoice: { min: number; max: number } | null = null
      if (minV !== null || maxV !== null) {
        if (minV === null || maxV === null || !Number.isFinite(minV) || !Number.isFinite(maxV) || minV >= maxV) return fail('myData.choices.badRange')
        rangeChoice = { min: minV, max: maxV }
      }
      let region: [number, number, number, number] | null = null
      if (regionOn.checked) {
        const r = [west, south, east, north].map((i) => Number(i.value)) as [number, number, number, number]
        if (!r.every(Number.isFinite) || r[0] >= r[2] || r[1] >= r[3] || r[1] < -90 || r[3] > 90 || r[0] < -180 || r[2] > 360) return fail('myData.choices.badRegion')
        region = r
      }
      let time: { start: string; end: string } | null = null
      if (report.time?.start && tStart.value && tEnd.value) {
        const s = Date.parse(`${tStart.value}:00Z`)
        const en = Date.parse(`${tEnd.value}:00Z`)
        if (!Number.isFinite(s) || !Number.isFinite(en) || en < s) return fail('myData.choices.badTime')
        time = { start: `${tStart.value}:00Z`, end: `${tEnd.value}:00Z` }
      }
      let tolerance: { quantile: number; abs: number } | null = null
      if (tolOn.checked) {
        const a = Number(abs.value)
        if (!(a > 0) || !Number.isFinite(a)) return fail('myData.choices.badTolerance')
        tolerance = { quantile: Number(quantile.value), abs: a }
      }
      const regridValue = (form.querySelector<HTMLInputElement>('input[name="regrid"]:checked')?.value ?? 'bilinear') as RegridMethod
      const defaultTime = report.time?.start && report.time.end ? { start: `${localInput(report.time.start)}:00Z`, end: `${localInput(report.time.end)}:00Z` } : null
      const choices = choicesFromForm({
        variable: variable.value,
        palette: palette.value,
        regrid: regridValue,
        time,
        defaultTime,
        region,
        range: rangeChoice,
        defaultRange: defaultRange(v),
        tolerance,
      })
      busy = true
      submit.disabled = true
      void submitChoices(j.id, choices).then((reply) => {
        busy = false
        submit.disabled = false
        if (!reply.ok) {
          error.textContent = errorText(reply.error)
          error.hidden = false
          if (reply.error === 'wrong_state') void loadJob(j.id)
          return
        }
        job = reply.value.job
        notice = { text: t('myData.choices.sent'), tone: 'info' }
        render()
        void loadJob(j.id)
      })
    })
    return form
  }

  function reviewBlock(j: JobView): HTMLElement {
    const box = h('section', { class: 'mydata-review', 'aria-label': t('myData.review.title') }, h('h4', null, t('myData.review.title')))
    const budget = parseErrorBudget(j.budget)
    const units = budget?.units ?? (j.choices?.variable && j.report ? variableUnits(j.report, String(j.choices.variable)) : null)
    if (budget) {
      box.append(h('p', { class: 'mydata-hint' }, t('myData.review.intro')), budgetTable(budget, units))
    } else {
      box.append(h('p', { class: 'mydata-notice is-error' }, t('myData.review.noBudget')))
    }
    const tol = j.choices?.tolerance && typeof j.choices.tolerance.quantile === 'number' && typeof j.choices.tolerance.abs === 'number' ? j.choices.tolerance : null
    const pass = j.tolerancePass
    box.append(h('p', { class: `mydata-verdict${pass === false ? ' is-fail' : pass === true ? ' is-pass' : ''}`, role: 'status' }, toleranceVerdict(tol, pass, units)))
    const canPublish = j.state === 'review' && pass === true && !!budget
    const publish = button('pl-tab-btn mydata-publish', t('myData.review.publish'), { disabled: !canPublish })
    box.append(h('div', { class: 'mydata-actions' }, publish))
    // Decision O3: say plainly what protects what before anything is published.
    if (j.state === 'review') box.append(h('p', { class: 'mydata-hint mydata-protection' }, t('myData.review.protection')))
    if (j.state === 'review' && !canPublish) box.append(h('p', { class: 'mydata-hint' }, t(pass === false ? 'myData.review.blocked' : 'myData.review.blockedNoBudget')))
    return box
  }

  function renderJob(id: string): HTMLElement {
    const wrap = h('div', { class: 'mydata-job', 'data-job': id })
    if (!job || job.id !== id) {
      wrap.append(head(t('myData.job.loading'), true), noticeLine() ?? h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading')))
      return wrap
    }
    const j = job
    const ds = list?.datasets.find((d) => d.id === j.dataset)
    wrap.append(head(ds?.title ?? j.fileName, true))
    wrap.append(h('p', { class: 'mydata-row-meta' }, [j.fileName, formatBytes(j.bytes), j.detectedType ?? ''].filter(Boolean).join(' · ')))
    wrap.append(stepper(j.state))
    wrap.append(h('p', { class: 'mydata-job-state', role: 'status', 'data-state': j.state }, t(STATE_LABEL[j.state])))
    const n = noticeLine()
    if (n) wrap.append(n)
    if (MOVING_STATES.includes(j.state) && j.progress !== null) {
      const pct = Math.round(Math.max(0, Math.min(1, j.progress)) * 100)
      wrap.append(h('div', { class: 'mydata-progress-bar', role: 'progressbar', 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': pct, 'aria-label': t('myData.job.progressAria') },
        h('span', { class: 'mydata-progress-fill', style: `inline-size: ${pct}%` })))
      if (j.step) wrap.append(h('p', { class: 'mydata-hint' }, t('myData.job.step', { step: j.step, percent: pct })))
    }
    if (j.state === 'uploaded') wrap.append(h('p', { class: 'mydata-hint' }, t('myData.job.waiting')))
    // The uploader (or the owner) can cancel an upload that was never opened, unless it is the one this page is still sending.
    const mayCancel = CANCELLABLE_JOB_STATES.includes(j.state) && (ds?.mine === true || list?.tenant?.role === 'owner') && upload?.job !== j.id
    if (mayCancel && !confirmCancel) {
      wrap.append(h('div', { class: 'mydata-actions' }, button('pl-tab-btn pl-tab-btn-quiet mydata-job-cancel', t('myData.job.cancel'), { 'data-job': j.id })))
    } else if (mayCancel) {
      wrap.append(h('div', { class: 'mydata-cancel-confirm', role: 'group', 'aria-label': t('myData.job.cancel') },
        h('p', null, t('myData.job.cancelConfirm')),
        h('div', { class: 'mydata-actions' },
          button('pl-tab-btn mydata-job-cancel-yes', t('myData.job.cancelYes'), { 'data-job': j.id }),
          button('pl-tab-btn pl-tab-btn-quiet mydata-job-cancel-keep', t('myData.job.cancelKeep')))))
    }
    if (j.error) {
      const known = FAILURE_TEXT[j.error.code]
      wrap.append(h('div', { class: 'mydata-failure', role: 'alert' },
        h('p', { class: 'mydata-failure-title' }, known ? t(known) : t('myData.failure.generic', { code: j.error.code })),
        j.error.detail ? h('p', { class: 'mydata-failure-detail' }, j.error.detail) : null))
    }
    if (j.sha256.browser) {
      const match = j.sha256.server ? j.sha256.server === j.sha256.browser : null
      wrap.append(h('p', { class: 'mydata-hash' },
        t(match === null ? 'myData.job.hashBrowser' : match ? 'myData.job.hashMatch' : 'myData.job.hashMismatch', { hash: `${j.sha256.browser.slice(0, 12)}…` })))
    }
    if (j.report) wrap.append(reportBlock(j.report))
    if (j.state === 'awaiting_choices' && j.report) wrap.append(choicesForm(j, j.report))
    if (j.state === 'review' || (j.state === 'published' && j.budget)) wrap.append(reviewBlock(j))
    if (j.state === 'published') {
      wrap.append(h('div', { class: 'mydata-actions' }, button('pl-tab-btn mydata-open', t('myData.open'), { 'data-id': j.dataset })))
    }
    return wrap
  }

  function renderDelete(d: DatasetView): HTMLElement {
    const input = h('input', { type: 'text', id: 'mydata-delete-confirm', class: 'mydata-input', autocomplete: 'off', spellcheck: 'false' })
    const confirm = button('pl-tab-btn mydata-delete-confirm', t('myData.delete.confirm'), { disabled: true, 'data-id': d.id })
    input.addEventListener('input', () => {
      confirm.disabled = input.value.trim() !== d.title.trim()
    })
    return h('div', { class: 'mydata-delete-view' },
      head(t('myData.delete.title'), true),
      h('p', { class: 'mydata-delete-name' }, d.title),
      h('p', null, t('myData.delete.explain')),
      h('p', null, t('myData.delete.custody')),
      h('label', { class: 'mydata-label', for: 'mydata-delete-confirm' }, t('myData.delete.typeTitle')),
      input,
      noticeLine() ?? '',
      h('div', { class: 'mydata-actions' }, confirm, button('pl-tab-btn pl-tab-btn-quiet mydata-delete-cancel', t('myData.delete.cancel'))))
  }

  /** A phase 4 / phase 3 panel, mounted once per subject and refreshed when shown. */
  function renderPanel(v: Extract<View, { kind: 'share' | 'members' | 'sources' }>): HTMLElement {
    const id = v.kind === 'share' ? v.dataset.id : null
    if (!panel || panel.kind !== v.kind || panel.id !== id) {
      const box = h('div', { class: 'mydata-panel' })
      const ctx = panelContext()
      const ctl = v.kind === 'share' ? mountSharePanel(box, ctx, v.dataset) : v.kind === 'members' ? mountMembersPanel(box, ctx) : mountSourcesPanel(box, ctx)
      panel = { kind: v.kind, id, ctl }
      panelBox.replaceChildren(box)
      ctl.refresh()
    }
    return panelBox
  }

  function render(): void {
    if (!shown) return
    let content: HTMLElement
    if (view.kind === 'locked') content = renderLocked(view.why)
    else if (view.kind === 'loading') content = h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading'))
    else if (view.kind === 'error') {
      content = h('div', { class: 'mydata-locked' },
        h('p', { class: 'mydata-notice is-error', role: 'alert' }, errorText(view.code)),
        button('pl-tab-btn pl-tab-btn-quiet mydata-retry', t('myData.retry')))
    } else if (view.kind === 'list') content = renderList()
    else if (view.kind === 'upload') content = renderUpload()
    else if (view.kind === 'job') content = renderJob(view.id)
    else if (view.kind === 'share' || view.kind === 'members' || view.kind === 'sources') content = renderPanel(view)
    else content = renderDelete(view.dataset)
    pane.replaceChildren(h('div', { class: 'mydata-tab' }, stepSlot, content))
  }

  // --------------------------------------------------------- actions

  function onUploadEvent(e: UploadEvent): void {
    if (!upload) return
    if (e.type === 'session') {
      upload.job = e.job
      upload.dataset = e.dataset
    } else if (e.type === 'progress') {
      upload.progress = e.progress
      upload.status = 'running'
      if (visible()) updateProgressInPlace()
      return
    } else if (e.type === 'paused') {
      upload.status = 'paused'
      upload.code = e.code
      notice = { text: t('myData.upload.pausedWhy', { why: errorText(e.code) }), tone: 'error' }
    } else if (e.type === 'done') {
      upload.status = 'done'
      notice = { text: t(e.dispatched ? 'myData.upload.done' : 'myData.upload.doneQueued'), tone: 'info' }
      const jobId = e.job
      upload = null
      list = null
      go({ kind: 'job', id: jobId })
      notice = { text: t(e.dispatched ? 'myData.upload.done' : 'myData.upload.doneQueued'), tone: 'info' }
      void loadList(true)
      return
    } else if (e.type === 'failed') {
      upload.status = 'failed'
      notice = { text: errorText(e.code), tone: 'error' }
      upload = null
      if (view.kind !== 'upload') view = { kind: 'upload' }
    } else if (e.type === 'cancelled') {
      const dataset = e.dataset
      upload = null
      notice = { text: t('myData.upload.cancelled'), tone: 'info' }
      // The site cancels the job: the key of what was sent is destroyed and the slot freed at once.
      // If it cannot (offline), what was sent stays unreadable and goes after 24 hours.
      if (e.job) {
        const jobId = e.job
        void cancelOwnUpload(jobId).then(async (r) => {
          let shredded = r.ok
          if (!r.ok && dataset && list?.tenant?.role === 'owner') shredded = (await deleteOwnDataset(dataset)).ok
          notice = { text: t(shredded ? 'myData.upload.cancelledShredded' : 'myData.upload.cancelledLater'), tone: 'info' }
          void loadList(true)
          render()
        })
      } else if (dataset) {
        notice = { text: t('myData.upload.cancelledLater'), tone: 'info' }
      }
      view = { kind: 'list' }
      void loadList(true)
    }
    if (visible()) render()
  }

  function startUpload(): void {
    const fileInput = pane.querySelector<HTMLInputElement>('#mydata-file')
    const nameInput = pane.querySelector<HTMLInputElement>('#mydata-name')
    const file = fileInput?.files?.[0]
    notice = null
    if (!file) {
      notice = { text: t('myData.upload.noFile'), tone: 'error' }
      render()
      return
    }
    const max = list?.quota?.jobBytes
    if (file.size === 0) {
      notice = { text: t('myData.upload.empty'), tone: 'error' }
      render()
      return
    }
    if (max !== undefined && file.size > max) {
      notice = { text: t('myData.error.tooLarge'), tone: 'error' }
      render()
      return
    }
    if (!acceptedFileName(file.name)) {
      notice = { text: t('myData.upload.unknownType'), tone: 'error' }
      render()
      return
    }
    upload = {
      handle: startOwnDataUpload(file, nameInput?.value ?? '', onUploadEvent),
      fileName: file.name,
      bytes: file.size,
      status: 'running',
      progress: null,
      job: null,
      dataset: null,
      code: null,
    }
    render()
  }

  async function open(id: string, trigger: HTMLElement): Promise<void> {
    const d = list?.datasets.find((x) => x.id === id) ?? shared.find((x) => x.id === id)
    trigger.setAttribute('aria-busy', 'true')
    const own = await ensureOwnDataset(id, d?.title)
    trigger.removeAttribute('aria-busy')
    if (!own.ok) {
      notice = { text: errorText(own.error), tone: 'error' }
      render()
      return
    }
    // Back on the list next time the tab is shown.
    stopPolling()
    view = { kind: 'list' }
    notice = null
    callbacks.onOpenDataset(id)
  }

  async function downloadCustody(id: string): Promise<void> {
    const scope = list?.tenant?.role === 'owner' ? 'tenant' : 'dataset'
    const reply = await custodyRecord(id, scope)
    if (!reply.ok) {
      notice = { text: errorText(reply.error), tone: 'error' }
      render()
      return
    }
    const url = URL.createObjectURL(reply.value)
    const a = h('a', { href: url, download: `ayni-custody-${id}.json`, class: 'sr-only' })
    document.body.append(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    notice = { text: t('myData.custody.saved'), tone: 'info' }
    render()
  }

  async function confirmDelete(id: string): Promise<void> {
    if (busy) return
    busy = true
    const reply = await guarded(stepSlot, 'dataset.delete', () => deleteOwnDataset(id))
    busy = false
    if (!reply.ok) {
      notice = reply.error === 'step_up_cancelled' ? null : { text: errorText(reply.error), tone: 'error' }
      render()
      return
    }
    forgetOwnDataset(id)
    list = list ? { ...list, datasets: list.datasets.filter((d) => d.id !== id) } : list
    go({ kind: 'list' })
    notice = { text: t('myData.delete.done'), tone: 'info' }
    render()
  }

  async function cancelJob(id: string, trigger: HTMLButtonElement): Promise<void> {
    if (busy) return
    busy = true
    trigger.disabled = true
    const reply = await cancelOwnUpload(id)
    busy = false
    confirmCancel = false
    if (!reply.ok) {
      notice = { text: errorText(reply.error), tone: 'error' }
      // A stale state (it moved on, or was already cancelled): show where it really is.
      await loadJob(id)
      render()
      return
    }
    // Back to the list: a shredded dataset's job no longer has a name to show.
    const dataset = reply.value.job.dataset
    if (reply.value.datasetShredded) {
      forgetOwnDataset(dataset)
      list = list ? { ...list, datasets: list.datasets.filter((d) => d.id !== dataset) } : list
    }
    go({ kind: 'list' })
    notice = { text: t('myData.upload.cancelledShredded'), tone: 'info' }
    render()
  }

  async function publish(j: JobView, trigger: HTMLButtonElement): Promise<void> {
    if (busy) return
    busy = true
    trigger.disabled = true
    const reply = await publishJob(j.id)
    busy = false
    if (!reply.ok) {
      notice = { text: errorText(reply.error), tone: 'error' }
      // A stale state: read the job again so the page shows where it really is.
      await loadJob(j.id)
      render()
      return
    }
    job = reply.value.job
    notice = { text: t('myData.review.published'), tone: 'info' }
    render()
    void loadList(true)
  }

  pane.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const btn = target.closest<HTMLButtonElement>('button')
    if (!btn || !pane.contains(btn)) return
    if (btn.classList.contains('mydata-back') || btn.classList.contains('mydata-delete-cancel')) return go({ kind: 'list' })
    if (btn.classList.contains('mydata-retry')) return void loadList()
    if (btn.classList.contains('mydata-signin')) return callbacks.onSignIn(btn)
    if (btn.classList.contains('mydata-plans')) return callbacks.onPlans(btn)
    if (btn.classList.contains('mydata-upload-open')) return go({ kind: 'upload' })
    if (btn.classList.contains('mydata-upload-start')) return startUpload()
    if (btn.classList.contains('mydata-cancel')) return upload?.handle.cancel()
    if (btn.classList.contains('mydata-resume') && upload) {
      upload.status = 'running'
      notice = null
      upload.handle.resume()
      return render()
    }
    if (btn.classList.contains('mydata-details') && btn.dataset.job) return go({ kind: 'job', id: btn.dataset.job })
    if (btn.classList.contains('mydata-open') && btn.dataset.id) return void open(btn.dataset.id, btn)
    if (btn.classList.contains('mydata-custody') && btn.dataset.id) return void downloadCustody(btn.dataset.id)
    if (btn.classList.contains('mydata-delete') && btn.dataset.id) {
      const d = list?.datasets.find((x) => x.id === btn.dataset.id)
      if (d) go({ kind: 'delete', dataset: d })
      pane.querySelector<HTMLInputElement>('#mydata-delete-confirm')?.focus()
      return
    }
    if (btn.classList.contains('mydata-delete-confirm') && btn.dataset.id) return void confirmDelete(btn.dataset.id)
    if (btn.classList.contains('mydata-open-shared') && btn.dataset.id) return void open(btn.dataset.id, btn)
    if (btn.classList.contains('mydata-share') && btn.dataset.id) {
      const d = list?.datasets.find((x) => x.id === btn.dataset.id)
      if (d) go({ kind: 'share', dataset: d })
      return
    }
    if (btn.classList.contains('mydata-members-open')) return go({ kind: 'members' })
    if (btn.classList.contains('mydata-sources-open')) return go({ kind: 'sources' })
    if (btn.classList.contains('mydata-publish') && job) return void publish(job, btn)
    if (btn.classList.contains('mydata-job-cancel')) {
      confirmCancel = true
      render()
      pane.querySelector<HTMLButtonElement>('.mydata-job-cancel-keep')?.focus()
      return
    }
    if (btn.classList.contains('mydata-job-cancel-keep')) {
      confirmCancel = false
      render()
      pane.querySelector<HTMLButtonElement>('.mydata-job-cancel')?.focus()
      return
    }
    if (btn.classList.contains('mydata-job-cancel-yes') && btn.dataset.job) return void cancelJob(btn.dataset.job, btn)
  })

  return {
    /** Draw the tab when it is shown; asks the site again every time (roles and plans can change). */
    refresh: () => {
      shown = true
      if (view.kind === 'job') {
        render()
        void loadJob(view.id)
        return
      }
      if (view.kind === 'upload' || view.kind === 'delete' || view.kind === 'share' || view.kind === 'members' || view.kind === 'sources') {
        render()
        return
      }
      if (!list) view = { kind: 'loading' }
      void loadList()
    },
  }
}
